import { Suspense, lazy, useEffect, useState } from "react"
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom"
import { PageTransition } from "@/components/ui/page-transition"
import { AppHeader } from "@/components/layout/AppHeader"
import { getSocket } from "@/lib/socket"
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  UsersRound,
  UserCog,
  Compass,
  FileBarChart2,
  Settings as SettingsIcon,
  GraduationCap,
  QrCode,
  AlertTriangle,
  Lock,
  LifeBuoy,
  ClipboardList,
} from "lucide-react"
import { hasModuleAccess, type ModuleKey } from "@/lib/module-access"
import { useToast } from "@/components/use-toast"
import { Button } from "@/components/ui/button"
import { useAuthStore, type AuthUser } from "@/stores/auth"
import { api } from "@/lib/api"
import { BrandLogo } from "@/components/BrandLogo"
import { exitImpersonation, readImpersonationBanner } from "@/lib/impersonation"
import { cn } from "@/lib/utils"

/**
 * THE ASSISTANT IS NOT IN THE ENTRY CHUNK, and that is a load-time decision
 * rather than a stylistic one. This file is imported statically by App.tsx, so
 * anything it imports statically ships to EVERY user of this SPA on first paint
 * — candidates sitting a proctored test included, none of whom can even see the
 * assistant. Measured: the whole tree below AssistantLauncher was riding in
 * index-*.js for all of them.
 *
 * Two levels, because the two halves are needed at different moments: the
 * launcher is a button that has to exist as soon as the shell renders, while
 * the panel behind it is only needed once somebody opens it. See the matching
 * note in AssistantLauncher.tsx.
 */
const AssistantLauncher = lazy(() =>
  import("@/components/assistant/AssistantLauncher").then((m) => ({
    default: m.AssistantLauncher,
  })),
)

// QR Assessment module — show the nav entry only when the feature flag is on.
// Toggle via frontend/.env: VITE_QR_MODULE_ENABLED=true.
const QR_ENABLED = import.meta.env.VITE_QR_MODULE_ENABLED === "true"

// Nav items optionally carry a moduleKey. When set, the sidebar checks
// the user's module flags and renders a lock icon (with muted styling)
// for any module the Super Admin hasn't enabled. The link still
// navigates — the route's ModuleGuard surfaces the friendly locked
// page from there, giving the user a "contact your administrator" path.
type NavItem = {
  to: string
  label: string
  icon: typeof LayoutDashboard
  moduleKey?: ModuleKey
  // When true, render the live action-center count as a number badge.
  showActionBadge?: boolean
  /**
   * Renders indented beneath the item above it, as something belonging to it.
   *
   * VISUAL NESTING ONLY, AND DELIBERATELY NOT A TREE. This nav has been flat
   * since it was written — one array, one map, no parent/child anywhere — and
   * Psychometrics is the first item that belongs under another. Building real
   * nesting for exactly one child would mean expand/collapse state, an
   * active-parent rule, aria-expanded and keyboard handling, all invented for a
   * list of one and used by nothing else.
   *
   * So this changes where the item LOOKS like it sits and nothing else. The
   * link, the module gate, the locked styling and the badge are the same code
   * for a nested item as for any other. If a second child ever appears, the
   * decision to build a real tree can be made then, with two things to justify
   * it rather than one.
   */
  nested?: boolean
}

const baseNavItems: readonly NavItem[] = [
  { to: "/client/dashboard", label: "Dashboard", icon: LayoutDashboard },
  {
    to: "/client/action-center",
    label: "Action Center",
    icon: ClipboardList,
    showActionBadge: true,
  },
  { to: "/client/projects", label: "My Assessments", icon: FolderKanban, moduleKey: "assessments" },
  // UNDER MY ASSESSMENTS, NOT BESIDE IT — a discovery change and nothing more.
  //
  // It was a top-level entry, on the reasoning that the instrument is not one
  // of this tenant's assessments and never appears in that list. Both halves of
  // that are still true, and none of it has changed: the route, the page and
  // every endpoint behind them are untouched, and assertPlatformInstrument is
  // still a different function from assertAssessmentOwned for the same reason
  // it always was.
  //
  // What changed is only where somebody LOOKS for it. An admin setting up an
  // assessment goes to My Assessments; an instrument they can enrol people into
  // is part of that job, whoever owns the rows underneath. Nesting says
  // "related to this" without saying "one of these" — which is exactly the
  // distinction the list itself must keep, since the instrument lives in the
  // host tenant's project and is excluded from that query by construction.
  {
    to: "/client/psychometrics",
    label: "Psychometrics",
    icon: Compass,
    moduleKey: "psychometrics",
    nested: true,
  },
  { to: "/client/multirater", label: "Multirater", icon: GraduationCap, moduleKey: "multirater" },
  ...(QR_ENABLED
    ? [{ to: "/client/qr", label: "QR Quiz", icon: QrCode, moduleKey: "qrQuiz" as const }]
    : []),
  { to: "/client/candidates", label: "Candidates", icon: Users },
  // Cohorts sits next to Candidates because it is the same axis — groups OF
  // candidates — not a master-data table, which is why it is not a Masters tab.
  { to: "/client/cohorts", label: "Cohorts", icon: UsersRound, moduleKey: "assessments" },
  { to: "/client/proctors", label: "Proctors", icon: UserCog, moduleKey: "proctoring" },
  { to: "/client/reports", label: "Reports", icon: FileBarChart2 },
  { to: "/client/masters", label: "Masters", icon: SettingsIcon },
  // Support is intentionally module-key-less so every client can raise
  // tickets regardless of which modules they have enabled.
  { to: "/client/support", label: "Support", icon: LifeBuoy },
]

const navItems = baseNavItems

function getInitials(input: string | undefined): string {
  if (!input) return "?"
  const parts = input.trim().split(/\s+/)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function ClientLayout({ children }: { children?: React.ReactNode }) {
  const navigate = useNavigate()
  // Subscribe only to the slices we actually use so the sidebar doesn't
  // re-render when unrelated parts of the store change.
  const user = useAuthStore((s) => s.user)
  const role = useAuthStore((s) => s.role)
  const isImpersonating = useAuthStore((s) => s.isImpersonating)
  // Banner display comes from the non-httpOnly `impersonation` cookie (set by
  // the enter endpoint), re-read on every render. isImpersonating (from /me) is
  // the reactive gate; the cookie carries the client's name/email.
  const impersonationBanner = readImpersonationBanner()
  const { toast } = useToast()
  const location = useLocation()

  // Action Center badge count — sum of all pending-action categories.
  // Polled lightly (on mount, on route change, and every 60s) via the
  // dedicated count endpoint so the sidebar number stays roughly live
  // without the cost of the full action-center payload.
  const [actionCount, setActionCount] = useState(0)
  useEffect(() => {
    if (!user?.id) return
    let cancelled = false
    const fetchCount = () => {
      api
        .get<{ data: { total: number } }>("/dashboard/action-center/count")
        .then((res) => {
          if (!cancelled) setActionCount(res.data.data.total)
        })
        .catch(() => {
          /* non-fatal — badge just won't update this tick */
        })
    }
    fetchCount()
    const id = window.setInterval(fetchCount, 60_000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
    // Re-fetch on navigation so acting on a task (then returning) updates
    // the badge promptly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, location.pathname])

  // Join the personal notification room so the header bell receives
  // real-time `notification:new` events for this admin.
  useEffect(() => {
    if (!user?.id) return
    const sock = getSocket()
    const join = () => sock.emit("admin:join", { clientId: user.id })
    join()
    sock.on("connect", join) // re-join on reconnect
    return () => {
      sock.off("connect", join)
    }
  }, [user?.id])

  // Hydrate module flags + refresh them every time the layout mounts.
  // Two reasons: (1) sessions persisted before this upgrade have no flags
  // in localStorage and would see every module locked until they
  // re-logged-in; (2) a Super Admin toggling a flag should propagate
  // without the client needing to manually sign out.
  const setAuth = useAuthStore((s) => s.setAuth)
  useEffect(() => {
    if (!user?.id || !role) return
    let cancelled = false
    api
      .get<{
        data: {
          moduleAssessments?: boolean
          moduleMultirater?: boolean
          moduleQrQuiz?: boolean
          moduleProctoring?: boolean
          moduleAiAssistant?: boolean
          modulePsychometrics?: boolean
          avatarUrl?: string | null
          jobTitle?: string | null
          companyName?: string | null
          fullName?: string
        }
      }>("/profile")
      .then((res) => {
        if (cancelled) return
        const fresh = res.data.data
        const merged: AuthUser = {
          ...user,
          fullName: fresh.fullName ?? user.fullName,
          avatarUrl: fresh.avatarUrl ?? user.avatarUrl,
          jobTitle: fresh.jobTitle ?? user.jobTitle,
          companyName: fresh.companyName ?? user.companyName,
          moduleAssessments: fresh.moduleAssessments,
          moduleMultirater: fresh.moduleMultirater,
          moduleQrQuiz: fresh.moduleQrQuiz,
          moduleProctoring: fresh.moduleProctoring,
          moduleAiAssistant: fresh.moduleAiAssistant,
          modulePsychometrics: fresh.modulePsychometrics,
        }
        setAuth({ role, user: merged })
      })
      .catch(() => {
        // Profile fetch failures are non-blocking; the user keeps whatever
        // flags they already have (worst case: stale until next refresh).
      })
    return () => {
      cancelled = true
    }
    // Re-run on user id change (e.g. impersonation start/exit).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id])

  const assistantEnabled = hasModuleAccess(user ?? null, role, "aiAssistant")

  // Derive a simple page title from the current route for the header.
  const headerTitle = (() => {
    const p = location.pathname
    if (p.startsWith("/client/dashboard")) return "Dashboard"
    if (p.startsWith("/client/projects")) return "My Assessments"
    if (p.startsWith("/client/multirater")) return "Multirater"
    if (p.startsWith("/client/qr")) return "QR Quiz"
    if (p.startsWith("/client/candidates")) return "Candidates"
    if (p.startsWith("/client/proctors")) return "Proctors"
    if (p.startsWith("/client/reports")) return "Reports"
    if (p.startsWith("/client/cohorts")) return "Cohorts"
    if (p.startsWith("/client/masters")) return "Masters"
    if (p.startsWith("/client/profile")) return "Profile"
    return "Prism Assessment"
  })()

  async function exitAndReturn() {
    await exitImpersonation()
    toast("Returned to Super Admin view")
    navigate("/super/dashboard", { replace: true })
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-subtle)]">
      <aside className="sidebar-light fixed inset-y-0 left-0 z-30 w-64 border-r border-[var(--color-border)] flex flex-col">
        {/* Logo */}
        <div className="px-5 py-5">
          <Link to="/client/dashboard" className="font-display inline-flex items-center gap-2.5">
            <BrandLogo size="sidebar" />
          </Link>
        </div>

        {/* Nav */}
        {/* nav-scroll: on short screens, tighter items and a visible scrollbar
            (design-system.css), so the last items are never silently hidden. */}
        <nav className="nav-scroll flex-1 px-3 pb-3 space-y-0.5 overflow-y-auto">
          <p className="text-overline px-3.5 pt-2 pb-1.5">Menu</p>
          {navItems.map(({ to, label, icon: Icon, moduleKey, showActionBadge, nested }) => {
            const locked = moduleKey
              ? !hasModuleAccess(user ?? null, role, moduleKey)
              : false
            const badge = showActionBadge && actionCount > 0 ? actionCount : null
            return (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    "nav-item group flex items-center gap-3 rounded-full px-3.5 py-2 text-sm font-medium",
                    // The whole of nesting: an indent, and a slightly smaller
                    // label so the eye reads it as subordinate rather than as
                    // another peer that happens to be shifted right.
                    nested && "ml-4 text-[13px]",
                    isActive
                      ? "is-active bg-[var(--color-accent)] text-white shadow-[var(--shadow-sm)]"
                      : locked
                        ? "text-[color:var(--color-text-muted)] hover:bg-slate-100"
                        : "text-[color:var(--color-text-secondary)] hover:bg-[var(--color-accent-light)] hover:text-[color:var(--color-accent-hover)]",
                  )
                }
                title={locked ? `${label} is not enabled for your account` : undefined}
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      className={cn(
                        "nav-icon h-5 w-5 shrink-0 transition-colors",
                        isActive
                          ? "text-white"
                          : locked
                            ? "text-[color:var(--color-text-muted)] opacity-60"
                            : "text-[color:var(--color-text-muted)] group-hover:text-[color:var(--color-accent-hover)]",
                      )}
                      strokeWidth={1.5}
                    />
                    <span className={cn("truncate", locked && !isActive ? "opacity-70" : "")}>
                      {label}
                    </span>
                    {badge != null ? (
                      <span
                        className={cn(
                          "ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold tabular-nums",
                          isActive
                            ? "bg-white/20 text-white"
                            : "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300",
                        )}
                      >
                        {badge > 99 ? "99+" : badge}
                      </span>
                    ) : null}
                    {locked ? (
                      <Lock
                        className={cn(
                          "ml-auto h-3.5 w-3.5 shrink-0",
                          isActive
                            ? "text-white/80"
                            : "text-slate-400 dark:text-slate-500",
                        )}
                        strokeWidth={2}
                      />
                    ) : null}
                  </>
                )}
              </NavLink>
            )
          })}
        </nav>

        {/* Minimal user block — name + role only. Profile actions
            (dark mode, sign out) live in the AppHeader profile dropdown
            so they aren't duplicated here. */}
        <div className="px-3 pb-4 pt-3 border-t border-[var(--color-border)]">
          <Link
            to="/client/profile"
            className="flex items-center gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-[var(--color-accent-light)]"
            title="View profile"
          >
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full text-xs font-semibold"
              style={{
                background: "var(--color-accent-light)",
                color: "var(--color-accent-hover)",
              }}
            >
              {user?.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={user.fullName ?? "Profile"}
                  className="h-full w-full object-cover"
                />
              ) : (
                getInitials(user?.fullName ?? user?.email)
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-body font-semibold text-foreground">
                {user?.fullName ?? user?.email}
              </div>
              <div className="truncate text-caption mt-0.5">
                {user?.jobTitle ?? "Client Admin"}
              </div>
            </div>
          </Link>
        </div>
      </aside>

      <main className="ml-64 min-h-screen">
        <AppHeader title={headerTitle} />
        {isImpersonating && impersonationBanner ? (
          <div className="bg-amber-100 border-b border-amber-300 px-6 py-2.5 text-amber-900 flex items-center gap-3 text-sm shadow-sm">
            <AlertTriangle className="h-4 w-4 shrink-0" strokeWidth={2} />
            <div className="flex-1 truncate">
              <strong>Impersonating:</strong> {impersonationBanner.name}{" "}
              <span className="opacity-80">({impersonationBanner.email})</span>
              <span className="hidden sm:inline opacity-70 ml-2">
                · all actions are performed as this client and are logged for audit
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={exitAndReturn}
              className="border-amber-400 bg-white text-amber-900 hover:bg-amber-50"
            >
              Exit Impersonation
            </Button>
          </div>
        ) : null}
        {/* pb-24 when the assistant launcher is mounted: it is fixed 24px from
            the bottom and 56px tall, so the last row of a page needs that much
            room to scroll clear of it. */}
        <div className={cn("p-8 max-w-7xl mx-auto", assistantEnabled && "pb-24")}>
          <PageTransition key={location.pathname}>{children ?? <Outlet />}</PageTransition>
        </div>
      </main>

      {/* THE ASSISTANT'S ENTRY POINT, mounted once for the whole client shell
          rather than per page — it has to be reachable from wherever the admin
          already is, since what it acts on is usually what they are looking at.
          Gated on the same hasModuleAccess() the sidebar uses, so a client
          without the module sees nothing at all: unlike the nav entries, there
          is no locked state to show, because there is no page to land on.
          Rendered OUTSIDE <main> so its fixed positioning is relative to the
          viewport and not affected by the ml-64 offset. */}
      {/* fallback={null} AND A LOCAL BOUNDARY. Local, because App.tsx's
          Suspense wraps <Routes> — letting this suspend up to there would blank
          the entire page to load a floating button. Null, because there is
          nothing useful to show in the gap: a skeleton where a button will
          appear reads as breakage, and the chunk is small. */}
      {assistantEnabled ? (
        <Suspense fallback={null}>
          <AssistantLauncher />
        </Suspense>
      ) : null}
    </div>
  )
}
